import _ from 'underscore';
import {
  AdsStudioODataError,
  getRemoveImageBackgroundAPIErrorProcessor,
  getGenerateImageBackgroundAPIErrorProcessor,
} from '../../error-handling';

export const getBackgroundChangeRepository = (oDataService) => {
  const removeImageBackground = (imageUrl) => {
    const promise = oDataService.removeImageBackground(imageUrl).then((res) => {
      const { imageUrl: returnedImageUrl, roi } = res || {};
      return { imageUrl: returnedImageUrl, roi };
    }).catch((err) => {
      throw new AdsStudioODataError({
        message: `RemoveImageBackground api failed, status: ${err.status}, imageUrl: ${imageUrl}`,
        originError: err,
        errorProcessor: getRemoveImageBackgroundAPIErrorProcessor,
      });
    });
    return promise;
  };

  const getImageSegmentations = (imageUrl) => {
    const promise = oDataService.getImageSegmentations(imageUrl).then((res) => {
      // base64 encoded mask
      const { MaskSets: rawMaskSetsBase64String, SelectedMasks: selectedMasksArray } = res || {};
      return { rawMaskSetsBase64String, selectedMasksArray };
    }).catch((err) => {
      throw new AdsStudioODataError({
        message: `GetImageSegmentation api failed, status: ${err.status}, imageUrl: ${imageUrl}`,
        originError: err,
        errorProcessor: getRemoveImageBackgroundAPIErrorProcessor,
      });
    });
    return promise;
  };

  const generateImageBackground = ({
    prompt,
    // it could be original image url, in such case, the selectedMasks contains the mask ids user selected
    // if it's the url returned from 'removeImageBackground', the selectedMasks should be null
    imageUrl,
    // array of mask ids, if it's empty [], it means no mask is selected
    // if it's null, it means no masks are applied
    maskSetsBase64,
    selectedMasks,
    outputImageWidth,
    outputImageHeight,
  }) => {
    const promise = oDataService.generateImageBackground({
      prompt, imageUrl, selectedMasks, outputImageWidth, outputImageHeight, maskSetsBase64,
    }).then((res) => {
      const { ImageUrl } = res || {};
      return { imageUrl: ImageUrl };
    }).catch((err) => {
      throw new AdsStudioODataError({
        message: `GenerateImageBackground api failed, status: ${err.status}, imageUrl: ${imageUrl}, selectedMasks: ${JSON.stringify(selectedMasks)}, outputImageWidth: ${outputImageWidth}, outputImageHeight: ${outputImageHeight}`,
        originError: err,
        errorProcessor: getRemoveImageBackgroundAPIErrorProcessor,
      });
    });
    return promise;
  };

  const generateMultipleImageBackgrounds = ({
    prompt,
    // it could be original image url, in such case, the selectedMasks contains the mask ids user selected
    // if it's the url returned from 'removeImageBackground', the selectedMasks should be null
    imageUrl,
    // array of mask ids, if it's empty [], it means no mask is selected
    // if it's null, it means no masks are applied
    maskSetsBase64,
    selectedMasks,
    outputImageWidth,
    outputImageHeight,
  }) => {
    const promise = oDataService.generateMultipleImageBackgrounds({
      prompt, imageUrl, selectedMasks, outputImageWidth, outputImageHeight, maskSetsBase64,
    }).then((res) => {
      const { TaskId } = res || {};
      return { taskId: TaskId };
    }).catch((err) => {
      throw new AdsStudioODataError({
        message: `GenerateMultipleImageBackgrounds api failed, status: ${err.status}, imageUrl: ${imageUrl}, selectedMasks: ${JSON.stringify(selectedMasks)}, outputImageWidth: ${outputImageWidth}, outputImageHeight: ${outputImageHeight}`,
        originError: err,
        errorProcessor: getGenerateImageBackgroundAPIErrorProcessor,
      });
    });
    return promise;
  };

  const getGenerateMultipleImageBackgroundsTaskStatus = (taskId) => {
    const promise = oDataService.getGenerateMultipleImageBackgroundsTaskStatus(taskId).then((res) => {
      const {
        Status: status, // NotStart, InProgress, Completed
        Progress: progress,
        RecommendedImages: recommendedImages,
      } = res || {};
      const images = _.map(recommendedImages, GeneratedBackgroundImage => ({
        imageUrl: GeneratedBackgroundImage.Url,
        modelSource: GeneratedBackgroundImage.ModelSource,
        source: GeneratedBackgroundImage.Source,
        recommendationId: GeneratedBackgroundImage.RecommendationId,
      }));

      if (status === 'Failed') {
        // throw an error with a UI predefined error code
        const error = new Error(`GeneratedBackgroundImagesStatus api failed, taskId: ${taskId}`);
        error.errors = [{
          Code: 'GenerationBackgroundFailed',
        }];
        throw error;
      }

      const isTaskDone = progress >= 100 || status === 'Completed';

      return {
        taskId, status, progress, images, isTaskDone,
      };
    }).catch((err) => {
      throw new AdsStudioODataError({
        message: `GeneratedBackgroundImagesStatus api failed, status: ${err.status}, taskId: ${taskId}`,
        originError: err,
        errorProcessor: getGenerateImageBackgroundAPIErrorProcessor,
      });
    });
    return promise;
  };

  const repository = {
    removeImageBackground,
    getImageSegmentations,
    generateImageBackground,
    generateMultipleImageBackgrounds,
    getGenerateMultipleImageBackgroundsTaskStatus,
  };
  return repository;
};
